import { zodResolver } from "@hookform/resolvers/zod";
import { type BaseSyntheticEvent, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { match } from "ts-pattern";
import { z } from "zod";

import { knowledgeApiSlice } from "api/knowledgeApi.slice";
import { ContentTypes } from "api/types/knowledge";
import { isCustomBaseQueryError } from "api/utils";
import { useTranslate } from "common/utilities/useTranslate";
import { getModalParams } from "domains/modal/selectors";
import type { ModalParams } from "domains/modal/types";

export const useAddKnowledgeForm = () => {
  const translate = useTranslate();

  const { topicId } = useSelector(getModalParams) as ModalParams<"AddKnowledge">;
  const [addedKnowledgeId, setAddedKnowledgeId] = useState<string | undefined>();

  const [addKnowledge, { isLoading: isAddKnowledgeLoading, isSuccess: isKnowledgeSuccessfullyAdded }] =
    knowledgeApiSlice.endpoints.addKnowledge.useMutation();

  const addKnowledgeFormSchema = z.object({
    url: z.string().url({ message: translate("app.add_knowledge.error.enter_valid_url") }),
    knowledgeType: z
      .object(
        { label: z.string(), value: z.nativeEnum(ContentTypes) },
        { required_error: translate("app.add_knowledge.error.enter_content_type") },
      )
      .required(),
  });

  const {
    handleSubmit,
    control,
    setError,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(addKnowledgeFormSchema),
    defaultValues: {
      url: "",
    },
  });

  const globalError = errors.root;

  const setGlobalError = useCallback((message: string) => setError("root", { message }), [setError]);

  const submitNewKnowledge = (event: BaseSyntheticEvent) =>
    handleSubmit(async (data) => {
      setAddedKnowledgeId(undefined);

      const result = await addKnowledge({
        link: data.url,
        contentType: data.knowledgeType?.value,
        topicIds: [topicId],
      });

      if (result.error && isCustomBaseQueryError(result.error)) {
        match(result.error.status)
          .with(409, () => setGlobalError(translate("app.add_knowledge.error.url_rejected_previously")))
          .with(422, () => setGlobalError(translate("app.add_knowledge.error.url_malformed")))
          .otherwise(() => setGlobalError(translate("app.add_knowledge.error.error_while_saving")));

        return;
      }

      setAddedKnowledgeId(result.data?.knowledge_id);
    })(event);

  return {
    isAddKnowledgeLoading,
    isSubmitSuccessful: isKnowledgeSuccessfullyAdded,
    addedKnowledgeId,
    control,
    globalError,
    submitNewKnowledge,
    setGlobalError,
  };
};
